import DashboardLayout from 'layouts/dashboard';
import { useSelector } from 'redux/store';
import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import Iconify from 'components/iconify/Iconify';
import OrderForm from '../../../../sections/@dashboard/user/order/OrderForm';
import { useRouter } from 'next/router';
import { setError } from 'redux/slices/global';
import { GetServerSidePropsContext } from 'next';
import axiosInstance from 'utils/axios';
import { IPack } from '../../../../@types/package';
import { IService, IServiceCreate } from '../../../../@types/service';
import { useSettingsContext } from 'components/settings';
import Markdown from 'components/markdown/Markdown';
import _ from 'lodash';
import { CreateDialog as FormEditPack } from 'sections/@dashboard/admin/Package/CreateDialog';
import { CreateDialog as FormEditService } from 'sections/@dashboard/admin/Service/CreateDialog';
import { setOpenDialogForm as openFormPack, updatePack } from 'redux/slices/package';
import { useSnackbar } from 'notistack';
import {
  setCurrentService,
  setOpenDialogForm as openFormService,
  updateService,
  getServiceById,
} from 'redux/slices/service';

OrderPage.getLayout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>;

export default function OrderPage({ packs, service }: { packs: IPack[]; service: IService }) {
  const { themeStretch } = useSettingsContext();
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const { user } = useSelector((state) => state.global);
  const { isOpenDialogForm, currentPack } = useSelector((state) => state.pack);
  const { isOpenDialogForm: isOpenFormService, currentService } = useSelector(
    (state) => state.service
  );
  const actions = (user?.permission && user?.permission['orderseeding']) ?? [];
  const packFilter =
    (packs.length > 0 &&
      _.orderBy(packs, ['displayname'], ['asc']).filter(
        (f) => actions.includes('add') || f?.status === 1
      )) ??
    [];

  const handleUpdate = async (payload: any) => {
    // Cập nhật Pack
    const [_, error] = await updatePack(payload);
    if (error) setError(error.errors);
    else {
      enqueueSnackbar('Cập nhật thành công', { variant: 'success' });
      openFormPack(false);
      router.push(router.asPath);
    }
  };

  const handleUpdateService = async (payload: IServiceCreate) => {
    const [_, error] = await updateService(payload);
    if (error) setError(error.errors);
    else {
      openFormService(false);
      enqueueSnackbar('Cập nhật thành công', { variant: 'success' });
      router.push(router.asPath);
    }
  };

  const handleEditService = async () => {
    const [_, error] = await getServiceById(service.id);
    if (_) {
      setCurrentService(_);
      openFormService(true);
    } else if (error) {
      setError(error.errors);
    }
  };

  return (
    <Container maxWidth={themeStretch ? false : 'lg'}>
      <Box style={{ flexDirection: 'row', display: 'flex', marginBottom: 15 }}>
        <Typography variant="h6" paragraph style={{ flex: 1 }}>
          {service?.displayname} - Tạo đơn hàng
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<Iconify icon={'material-symbols:list-alt-outline-rounded'} />}
          onClick={() =>
            router.push(
              `/orderseeding/${service.name?.toLocaleLowerCase()}/${
                router.query.serviceId
              }/list?page=0&limit=10&&search=`
            )
          }
        >
          Danh sách đơn hàng
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 2 }}>
            {packs.length > 0 && <OrderForm packs={packFilter || []} service={service} />}
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ minHeight: 500, p: 1 }}>
            {actions.length > 0 && (
              <Tooltip title="Click để sửa dịch vụ">
                <IconButton color="primary" onClick={handleEditService}>
                  <Iconify icon="akar-icons:edit" />
                </IconButton>
              </Tooltip>
            )}
            <Markdown children={service?.description ?? ''} />
          </Card>
        </Grid>
      </Grid>

      <FormEditPack
        open={isOpenDialogForm}
        onClose={() => openFormPack()}
        currentData={currentPack}
        onCreate={(payload) => {}}
        onUpdate={(payload) => handleUpdate(payload)}
      />

      <FormEditService
        open={isOpenFormService}
        onClose={() => openFormService(false)}
        currentData={currentService}
        onCreate={(payload) => {}}
        onUpdate={(payload) => handleUpdateService(payload)}
      />
    </Container>
  );
}

export async function getServerSideProps({ params, res }: GetServerSidePropsContext) {
  res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=60');
  try {
    const { data } = await axiosInstance.get<{ packs: IPack[]; service: IService }>(
      `/api/v1/pack/url/${params?.serviceId}`
    );
    return { props: { packs: data.packs, service: data.service } };
  } catch (e) {
    return { props: { packs: [], service: null, errors: e.errors } };
  }
}
